import Fuse from 'fuse.js';

/**
 * searchService.js — Production implementation (A4b)
 *
 * Fuse.js index with weighted fields.
 * TypeAhead suggestions (max 5, grouped by category).
 * Full search results with match highlighting.
 * Category grouping for dropdown display.
 *
 * Source patterns: ToolFinder v4 useSearch + Catalog search
 * Weights: name 0.4, category 0.25, description 0.2, specs 0.1, sku 0.05
 */

// ============================================================================
// Fuse.js Configuration
// ============================================================================

const FUSE_OPTIONS = {
  keys: [
    { name: 'name', weight: 0.4 },
    { name: 'description', weight: 0.2 },
    { name: 'category', weight: 0.25 },
    { name: 'categoryName', weight: 0.15 },
    { name: 'sku', weight: 0.05 },
    { name: 'itemCode', weight: 0.05 },
    { name: 'thread', weight: 0.1 },
    { name: 'thread1', weight: 0.05 },
    { name: 'diameter', weight: 0.05 },
    { name: 'competitorRef', weight: 0.1 },
    { name: 'searchKeywords', weight: 0.1 },
  ],
  threshold: 0.35,
  distance: 200,
  includeScore: true,
  includeMatches: true,
  minMatchCharLength: 2,
  ignoreLocation: false,
  findAllMatches: false,
  useExtendedSearch: false,
};

// ============================================================================
// Index Management
// ============================================================================

let fuseInstance = null;
let indexedProducts = [];

/**
 * Build the Fuse.js search index from product data
 * Call this after loading products from dataService
 *
 * @param {Array} products - Flat array of all products (from loadAllProducts)
 * @returns {Fuse} - The Fuse instance (also stored internally)
 */
export function buildIndex(products) {
  if (!products || products.length === 0) {
    console.warn('[searchService] buildIndex called with empty products');
    fuseInstance = null;
    indexedProducts = [];
    return null;
  }

  indexedProducts = products;
  fuseInstance = new Fuse(products, FUSE_OPTIONS);
  console.info(`[searchService] Index built: ${products.length} products`);
  return fuseInstance;
}

/**
 * Check if index is ready
 */
export function isIndexReady() {
  return fuseInstance !== null && indexedProducts.length > 0;
}

/**
 * Get the count of indexed products
 */
export function getIndexSize() {
  return indexedProducts.length;
}

// ============================================================================
// Search Functions
// ============================================================================

/**
 * Full search — returns scored results with match metadata
 *
 * @param {string} query - Search query
 * @param {Object} options
 * @param {number} options.limit - Max results (default 50)
 * @param {string} options.category - Filter by category ID
 * @param {string} options.domain - Filter by domain ('tophammer' | 'dth')
 * @returns {Array} - Array of { ...product, _score, _matches }
 */
export function search(query, { limit = 50, category, domain } = {}) {
  if (!fuseInstance || !query || query.trim().length < 2) return [];

  let results = fuseInstance.search(query.trim(), { limit: limit * 2 }); // over-fetch for filtering

  // Post-filter by category or domain if specified
  if (category) {
    results = results.filter(r =>
      r.item.category === category || r.item._categoryId === category
    );
  }
  if (domain) {
    results = results.filter(r => r.item._domain === domain);
  }

  return results.slice(0, limit).map(r => ({
    ...r.item,
    _score: r.score,
    _matches: r.matches,
  }));
}

/**
 * TypeAhead suggestions — lightweight, max 5, for dropdown
 *
 * @param {string} query - Partial search query
 * @param {number} limit - Max suggestions (default 5)
 * @returns {Array} - Array of { ...product, _score }
 */
export function getTypeAheadSuggestions(query, limit = 5) {
  if (!fuseInstance || !query || query.trim().length < 2) return [];

  const results = fuseInstance.search(query.trim(), { limit });
  return results.map(r => ({
    ...r.item,
    _score: r.score,
  }));
}

/**
 * Group search results by category (for dropdown display)
 * Follows ToolFinder pattern: category header → product list
 *
 * @param {Array} results - Results from search() or getTypeAheadSuggestions()
 * @returns {Object} - { categoryId: [...products] }
 */
export function groupByCategory(results) {
  const groups = {};
  results.forEach(product => {
    const cat = product.category || product._categoryId || product._domain || 'other';
    if (!groups[cat]) {
      groups[cat] = [];
    }
    groups[cat].push(product);
  });
  return groups;
}

// ============================================================================
// Category Labels (for display in grouped results)
// ============================================================================

const CATEGORY_LABELS = {
  'th-bits': 'TH Bits',
  'th-rods': 'TH Rods',
  'th-shanks': 'TH Shanks',
  'th-couplings': 'TH Couplings',
  'dth-bits': 'DTH Bits',
  'dth-hammers': 'DTH Hammers',
  'dth-drill-pipes': 'DTH Drill Pipes',
  'rotary-bits': 'Rotary Bits',
  // Rotary bits family subcategories (5 slugs from products-rotary-bits.json — Group R1)
  'rotary-bits-rr240': 'RR240 Bits',
  'rotary-bits-rr340': 'RR340 Bits',
  'rotary-bits-rr440': 'RR440 Bits',
  'rotary-bits-rr450': 'RR450 Bits',
  'rotary-bits-rr441': 'RR441 Bits',
  // Rotary subcategories (11 slugs from products-rotary.json)
  'rotary-drill-pipes': 'Rotary Drill Pipes',
  'rotary-adaptors': 'Rotary Adaptors',
  'rotary-safety-adaptors': 'Rotary Safety Adaptors',
  'rotary-stabilizers': 'Rotary Stabilizers',
  'rotary-deck-bushings': 'Rotary Deck Bushings',
  'rotary-lifting-bails-plugs': 'Lifting Bails & Plugs',
  'rotary-bit-baskets': 'Bit Baskets',
  'rotary-other-accessories': 'Other Rotary Accessories',
  'rotary-shock-absorbers': 'Shock Absorbers',
  'rotary-boom-adaptors-new': 'Boom Adaptors',
  'rotary-boom-drill-pipes-new': 'Boom Drill Pipes',
  'raiseboring': 'Raiseboring',
  // Raiseboring subcategories (Group R2, products-raiseboring.json — 2026-07-08)
  'raiseboring-cutters': 'Raiseboring Cutters',
  'raiseboring-reamer-heads': 'Raiseboring Reamer Heads',
  'raiseboring-reamer-heads-integral':   'Integral Reamer Heads',
  'raiseboring-reamer-heads-segmented':  'Segmented Reamer Heads',
  'raiseboring-reamer-heads-extendable': 'Extendable Reamer Heads',
  'raiseboring-stems': 'Raiseboring Stems',
  // Services (Group S1 — resharpening machines + cups, 2026-07-08)
  'services': 'Services',
  'services-resharpening': 'Resharpening',
  'services-resharpening-machines': 'Grinding Machines',
  'services-resharpening-cups': 'Grinding Cups',
  'top-hammer': 'Top Hammer',
  'down-the-hole': 'Down the Hole',
  rotary: 'Rotary',
  tophammer: 'Top Hammer',
  dth: 'Down the Hole',
  other: 'Other',
};

/**
 * Get display label for a category ID
 * @param {string} categoryId
 * @returns {string}
 */
export function getCategoryLabel(categoryId) {
  return CATEGORY_LABELS[categoryId] || categoryId;
}

// ============================================================================
// Highlight helpers
// ============================================================================

/**
 * Get highlighted match ranges for a field
 * Useful for rendering bold/highlighted text in search results
 *
 * @param {Array} matches - Fuse.js match array from result._matches
 * @param {string} fieldName - Field name to get highlights for
 * @returns {Array} - Array of [start, end] index pairs
 */
export function getHighlightRanges(matches, fieldName) {
  if (!matches) return [];
  const fieldMatch = matches.find(m => m.key === fieldName);
  if (!fieldMatch) return [];
  return fieldMatch.indices.filter(([start, end]) => end - start >= 1);
}

// ============================================================================
// Reset
// ============================================================================

/**
 * Clear the search index
 */
export function clearIndex() {
  fuseInstance = null;
  indexedProducts = [];
}
